import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Activity, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useGroups } from '@/hooks/useGroups';
import { useActivities, useActivitiesRealtimeSubscription } from '@/hooks/useActivities';
import { ActivityFilters } from '@/components/activities/ActivityFilters';
import { ActivityItem } from '@/components/activities/ActivityItem';
import { formatActivity, navigateToActivity } from '@/lib/activityFormatter';
import type { ActivityFilterCategory } from '@/api/activities';
import { useTranslation } from 'react-i18next';

export function ActivitiesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: groups } = useGroups();

  const [category, setCategory] = useState<ActivityFilterCategory>('all');
  const [groupId, setGroupId] = useState<string | undefined>();

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useActivities(category, groupId, groups?.map(g => g.id));

  // Live updates
  useActivitiesRealtimeSubscription();

  // Flatten pages into a single array of formatted activities
  const activities = useMemo(() => {
    if (!data || !user) return [];
    return data.pages.flatMap(page => page.map(row => formatActivity(row, user.id)));
  }, [data, user]);

  // Build group options for filter chips
  const groupOptions = useMemo(
    () =>
      groups?.map(g => ({
        id: g.id,
        name: g.name,
        color: g.color ?? '#6366F1',
      })),
    [groups],
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold text-text-primary">{t('web:common.activity')}</h1>

      <ActivityFilters
        category={category}
        onCategoryChange={c => { setCategory(c); }}
        groups={groupOptions}
        selectedGroupId={groupId}
        onGroupChange={setGroupId}
      />

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {t('web:activitiesPage.failedToLoadActivities', { message: error instanceof Error ? error.message : t('web:common.unknownError') })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      )}

      {/* Activity list */}
      {!isLoading && activities.length > 0 && (
        <div className="divide-y divide-border-tertiary overflow-hidden rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary">
          {activities.map(a => (
            <ActivityItem
              key={a.id}
              activity={a}
              onClick={() => navigateToActivity(a, navigate)}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 rounded-lg border border-border-secondary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover disabled:opacity-50"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common:loading')}
              </>
            ) : (
              t('web:common.loadMore')
            )}
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activities.length === 0 && (
        <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
            <Activity className="h-6 w-6 text-text-quaternary" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-text-primary">{t('home:leaderboard.emptyFrequent')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('web:activitiesPage.activitiesWillAppearHereAsYou')}
          </p>
        </div>
      )}
    </div>
  );
}
